import React, { useState } from 'react';
import { USER_ROLE, USER_ROLE_ENUM, USER_TYPES, USER_TYPES_ENUM } from '../../Constants/Constants';
import { INVITE_USER_ENDPOINT } from '../../urls';
import { Alert } from '../Alert';
import useFormStatus from '../../Hooks/useFormStatus';
import { fetchApiData } from '../../Utils/ApiCalls';
import LoaderCircle from '../LoaderCircle';
import { Button, Input, Select, Typography, RadioGroup } from '@attentive-platform/stem-ui';
import { ConstructionUserFlag, WelcomeEmailFlag } from '../InviteUsers/Flag';
import { InviteMultipleUsers } from '../InviteUsers/InviteMultipleUsers';

const INVITE_USER_ACTION_TYPE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple'
};

const InviteUserForm = () => {
  const [loading, setLoading] = useState(false);
  const { errorMessage, successMessage, handleApiResult } = useFormStatus();

  const initialFormData = {
    email: '',
    admin_email: '',
    is_lite_user: false,
    is_construction_signup: false,
    send_welcome_email: false,
    first_name: '',
    last_name: '',
    phone_number: '',
    role: USER_ROLE.TEAM_MEMBER
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleDataChange = (val, field) => {
    setFormData({
      ...formData,
      [field]: val
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Make the POST request to the API with the form data
    setLoading(true);
    formData.email = formData.email.toLowerCase();
    formData.admin_email = formData.admin_email.toLowerCase();
    const submissionData = { ...formData };
    submissionData.email = submissionData.email.toLowerCase();
    submissionData.admin_email = submissionData.admin_email.toLowerCase();

    const response = await fetchApiData({
      endpoint: INVITE_USER_ENDPOINT,
      data: submissionData
    });
    handleApiResult(response);
    setLoading(false);
    if (response.error === null) setFormData(initialFormData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Alert errorMessage={errorMessage} successMessage={successMessage} />

      <div className='grid grid-cols-2 gap-4 mb-4'>
        <Input
          label='First Name'
          type='text'
          value={formData.first_name}
          onChange={e => handleDataChange(e.target.value, 'first_name')}
          placeholder='Enter first name'
          required
          fullWidth
        />
        <Input
          label='Last Name'
          type='text'
          value={formData.last_name}
          onChange={e => handleDataChange(e.target.value, 'last_name')}
          placeholder='Enter last name'
          required
          fullWidth
        />
      </div>

      <div className='grid grid-cols-2 gap-4 mb-4'>
        <Input
          label='User Email'
          type='email'
          value={formData.email}
          onChange={e => handleDataChange(e.target.value, 'email')}
          placeholder='Enter user email'
          required
          fullWidth
        />
        <Input
          label='Password'
          type='text'
          value={formData.password}
          onChange={e => handleDataChange(e.target.value, 'password')}
          placeholder='Enter password'
          required
          fullWidth
        />
      </div>

      <div className='mb-4'>
        <Input
          label='Admin Email'
          type='email'
          value={formData.admin_email}
          onChange={e => handleDataChange(e.target.value, 'admin_email')}
          placeholder='Enter associated admin email'
          required
          fullWidth
        />
      </div>

      <div className='mb-4'>
        <Input
          label='Phone number'
          type='text'
          value={formData.phone_number}
          onChange={e => handleDataChange(e.target.value, 'phone_number')}
          placeholder='Enter phone number'
          required
          fullWidth
        />
      </div>

      <div className='mb-4'>
        <Typography variant='body3' className='block font-medium'>
          User type
        </Typography>
        <Select
          value={formData.is_lite_user}
          onChange={value => handleDataChange(value, 'is_lite_user')}
          required
          fullWidth
          placeholder='Select user type'
          items={Object.entries(USER_TYPES).map(([key, value]) => ({
            label: USER_TYPES_ENUM[value],
            value: value
          }))}
        />
      </div>

      <div className='mb-2'>
        <Typography variant='body3' className='block font-medium'>
          User Role
        </Typography>
        <Select
          value={formData.role}
          onChange={value => handleDataChange(value, 'role')}
          required
          fullWidth
          placeholder='Select user role'
          items={Object.entries(USER_ROLE).map(([key, value]) => ({
            label: USER_ROLE_ENUM[value],
            value: value
          }))}
        />
      </div>
      <div className='flex gap-4 mb-2'>
        <ConstructionUserFlag
          isSet={formData.is_construction_signup}
          onChange={obj => handleDataChange(obj.is_construction_signup, 'is_construction_signup')}
        />
        <WelcomeEmailFlag
          isSet={formData.send_welcome_email}
          onChange={obj => handleDataChange(obj.send_welcome_email, 'send_welcome_email')}
        />
      </div>
      <div className='flex justify-end'>
        <Button color='primary' size='medium' variant='contained' type='submit' disabled={loading}>
          {loading ? <LoaderCircle /> : 'Invite'}
        </Button>
      </div>
    </form>
  );
};

export const InviteUser = () => {
  const [inviteUserActionType, setInviteUserActionType] = useState(INVITE_USER_ACTION_TYPE.SINGLE);

  return (
    <>
      <div className='mb-3'>
        <RadioGroup
          value={inviteUserActionType}
          onChange={(event, newValue) => setInviteUserActionType(newValue)}
          radioGroup={[
            { label: 'Invite Single User', value: INVITE_USER_ACTION_TYPE.SINGLE },
            { label: 'Invite Multiple Users', value: INVITE_USER_ACTION_TYPE.MULTIPLE }
          ]}
          row
        />
      </div>
      {inviteUserActionType === INVITE_USER_ACTION_TYPE.SINGLE ? <InviteUserForm /> : <InviteMultipleUsers />}
    </>
  );
};
